import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { queryClient, trpc } from "~/utils/trpc";
import { JobDetailsPage, type JobDetailsTabView } from "./jobs/-pages/jobDetails";
import { z } from "zod";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { useJobLabels } from "~/hooks/useJobLabels";
import { sanitizeDocumentTitlePart, useDocumentTitle } from "~/utils/document-title";

const jobDetailSearchSchema = z.object({
  view: z
    .enum([
      "details",
      "applications",
      "activity",
      "notes",
      "client",
      "submissions",
      "documents",
      "placements",
      "chat-projects",
    ])
    .optional()
    .default("details"),
});

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/jobs/$jobId")({
  staleTime: 0,
  validateSearch: jobDetailSearchSchema,
  loader: async ({ params }) => {
    const job = await queryClient.fetchQuery(trpc.jobs.getById.queryOptions({ id: params.jobId }));
    return { job };
  },
  component: JobDetailsComponent,
});

function JobDetailsComponent() {
  const { job } = Route.useLoaderData();
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const navigate = useNavigate();
  const search = useSearch({ from: "/_dashboard/$datasetSlug/data/jobs/$jobId" });
  const activeTab = (search.view as JobDetailsTabView) ?? "details";
  const jobTitle = sanitizeDocumentTitlePart(job.title, "Job");
  useDocumentTitle(jobTitle, "Job");
  const { plural } = useJobLabels();

  useBreadCrumbs([
    { title: plural, link: `/${datasetSlug}/data/jobs` },
    { title: jobTitle, link: `/${datasetSlug}/data/jobs/${job.id}` },
  ]);

  const handleTabChange = (value: JobDetailsTabView) => {
    navigate({
      to: "/$datasetSlug/data/jobs/$jobId",
      params: { datasetSlug, jobId: job.id },
      search: { view: value },
    });
  };

  const handleEditClick = () => {
    navigate({
      to: "/$datasetSlug/data/jobs/edit/$id",
      params: { datasetSlug, id: job.id },
    });
  };

  return <JobDetailsPage job={job} activeTab={activeTab} onTabChange={handleTabChange} onEditClick={handleEditClick} />;
}
